import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Download, Copy, QrCode, Palette } from 'lucide-react';
import { motion } from 'framer-motion';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { QRPoster, QRPosterHandle, PosterTheme } from './QRPoster';
import { clayTapScale } from '../../constants/animations';
import { getPortalUrl } from '../../lib/portal';
import styles from './PosterModal.module.css';

// ── Theme Swatch Metadata ──────────────────────────────────────────────────────
const THEME_SWATCHES: { key: PosterTheme; label: string; color: string }[] = [
  { key: 'indigo', label: 'Red', color: '#ef4444' },
  { key: 'emerald', label: 'Emerald', color: '#34d399' },
  { key: 'rose', label: 'Rose', color: '#fb7185' },
  { key: 'amber', label: 'Amber', color: '#fbbf24' },
  { key: 'slate', label: 'Slate', color: '#94a3b8' },
];

interface PosterModalProps {
  isOpen: boolean;
  onClose: () => void;
  albumName: string;
  eventName: string;
  inviteToken: string;
}

/**
 * PosterModal — displays a preview of the QR poster with download and
 * copy-link actions. Uses the `xl` modal size to give the 16:9 poster
 * enough room while staying within the viewport.
 */
export function PosterModal({
  isOpen,
  onClose,
  albumName,
  eventName,
  inviteToken,
}: PosterModalProps) {
  const posterRef = useRef<QRPosterHandle>(null);
  const [downloading, setDownloading] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<PosterTheme>('indigo');
  const [customText, setCustomText] = useState('');

  const inviteUrl = `${getPortalUrl('viewer')}/join/${inviteToken}`;

  // ── Download poster as PNG ────────────────────────────────────────────────
  const handleDownload = async () => {
    const canvas = posterRef.current?.getCanvas();
    if (!canvas) {
      toast.error('Poster not ready yet');
      return;
    }

    setDownloading(true);
    try {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/png'),
      );
      if (!blob) {
        toast.error('Failed to generate poster');
        return;
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Sanitise album name for filename
      const safeName = albumName
        .replace(/[^a-zA-Z0-9\s-_]/g, '')
        .replace(/\s+/g, '-')
        .toLowerCase();
      a.download = `${safeName}-poster.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Poster downloaded!');
    } catch {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  // ── Copy invite link ──────────────────────────────────────────────────────
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success('Invite link copied!');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="QR Poster" size="xl">
      <div className={styles['wrapper']}>
        {/* Customization panel */}
        <div className={styles['customization-panel']}>
          {/* Theme selector */}
          <div className={styles['theme-group']}>
            <label className={styles['theme-label']}>
              <Palette className={styles['icon-md']} />
              Theme
            </label>
            <div className={styles['theme-swatches']}>
              {THEME_SWATCHES.map((swatch) => (
                <button
                  key={swatch.key}
                  type="button"
                  title={swatch.label}
                  onClick={() => setSelectedTheme(swatch.key)}
                  className={[
                    styles['swatch'],
                    styles['swatch-dynamic'],
                    selectedTheme === swatch.key ? `${styles['swatch-selected']} ${styles['swatch-dynamic-selected']}` : '',
                  ].filter(Boolean).join(' ')}
                  style={{ '--swatch-color': swatch.color } as React.CSSProperties}
                  aria-label={`${swatch.label} theme`}
                  aria-pressed={selectedTheme === swatch.key}
                />
              ))}
            </div>
          </div>

          {/* Custom text input */}
          <div className={styles['text-input-group']}>
            <label
              htmlFor="poster-custom-text"
              className={styles['text-input-label']}
            >
              Instruction text
            </label>
            <input
              id="poster-custom-text"
              type="text"
              maxLength={80}
              placeholder="Scan to view live photos"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              className={styles['text-input']}
            />
          </div>
        </div>

        {/* Poster preview */}
        <motion.div
          className={styles['poster-preview']}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <QRPoster
            ref={posterRef}
            albumName={albumName}
            eventName={eventName}
            inviteToken={inviteToken}
            theme={selectedTheme}
            customText={customText}
          />
        </motion.div>

        {/* Info text */}
        <p className={styles['info-text']}>
          Print or share this poster so guests can scan the QR code and view your live album.
        </p>

        {/* Action buttons */}
        <div className={styles['actions-row']}>
          <div className={styles['action-btn-wrap']}>
            <motion.div whileTap={clayTapScale}>
              <Button
                variant="primary"
                size="lg"
                loading={downloading}
                onClick={handleDownload}
                fullWidth
              >
                <Download className={styles['icon-btn-lg']} />
                Download Poster
              </Button>
            </motion.div>
          </div>

          <div className={styles['action-btn-wrap']}>
            <motion.div whileTap={clayTapScale}>
              <Button
                variant="secondary"
                size="lg"
                onClick={handleCopyLink}
                fullWidth
              >
                <Copy className={styles['icon-btn-lg']} />
                Copy Invite Link
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Invite URL preview */}
        <div className={styles['invite-url']}>
          <QrCode className={`${styles['icon-lg']} ${styles['invite-url-icon']}`} />
          <span className={styles['invite-url-text']}>
            {inviteUrl}
          </span>
        </div>
      </div>
    </Modal>
  );
}
